import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  LinearProgress,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function formatMoney(value) {
  const numberValue = Number(value || 0);

  if (numberValue >= 1000000) {
    return thousandsSeparator((numberValue / 1000000).toFixed(1)) + "M";
  }

  if (numberValue >= 1000) {
    return thousandsSeparator((numberValue / 1000).toFixed(1)) + "K";
  }

  return thousandsSeparator(numberValue.toFixed(0));
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

class PromotionsPerformanceByPromotion extends React.Component {

  state = {
    order: 'asc',
    orderBy: null,
  };

  handleRequestSort = (property) => {
    const isAscending = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAscending ? 'desc' : 'asc', orderBy: property });
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const stringValue = String(value);
    const trimmedValue = stringValue.trimStart();
    const numericValue = /^-?\d+(?:\.\d+)?%?$/.test(trimmedValue.trim());
    const safeValue = !numericValue && /^[=+\-@\t\r]/.test(trimmedValue) ? `'${stringValue}` : stringValue;
    const cleanValue = safeValue.replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.props.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const meta = this.props.meta || {};
    const currency = meta.currency || 'RON';

    const totalClaimsCreated = rows.reduce(
      (sum, row) => sum + Number(row.claims_created || 0),
      0
    );

    const totalClaimsConsumed = rows.reduce(
      (sum, row) => sum + Number(row.claims_consumed || 0),
      0
    );

    const totalIssuedClaimsConsumed = rows.reduce(
      (sum, row) => sum + Number(row.issued_claims_consumed_by_period_end || 0),
      0
    );

    const totalOrdersWithClaims = rows.reduce(
      (sum, row) => sum + Number(row.orders_with_claims || 0),
      0
    );

    const totalSalesFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.sales_from_claims || 0),
      0
    );

    const totalDiscountFromClaims = rows.reduce(
      (sum, row) => sum + Number(row.discount_from_claims || 0),
      0
    );

    const weightedClaimConversion = totalClaimsCreated > 0
      ? (totalIssuedClaimsConsumed / totalClaimsCreated) * 100
      : 0;

    const totalGrossSalesFromClaims = totalSalesFromClaims + totalDiscountFromClaims;
    const weightedDiscountRate = totalGrossSalesFromClaims > 0
      ? (totalDiscountFromClaims / totalGrossSalesFromClaims) * 100
      : 0;

    const averageDiscountPerPromoOrder = totalOrdersWithClaims > 0
      ? totalDiscountFromClaims / totalOrdersWithClaims
      : 0;

    const highCostPromotions = rows.filter(
      (row) => Number(row.discount_rate || 0) > 15
    ).length;

    const headers = [
      { key: 'promotion_name', label: 'Promotion' },
      { key: 'claims_created', label: 'Claims' },
      { key: 'claims_consumed', label: 'Used' },
      { key: 'issued_claim_conversion', label: 'Issued conversion %' },
      { key: 'orders_with_claims', label: 'Orders' },
      { key: 'sales_from_claims', label: `Net influenced sales (${currency})` },
      { key: 'discount_from_claims', label: `Discount (${currency})` },
      { key: 'discount_rate', label: 'Discount rate (% of gross promo sales)' },
      { key: 'average_discount', label: `Avg. discount (${currency})` },
    ];

    const csvRows = [
      ['Promotion performance'].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Date from', meta.dateFrom || ''].map((value) => this.csvValue(value)).join(','),
      ['Date to', meta.dateTo || ''].map((value) => this.csvValue(value)).join(','),
      ['Timezone', meta.timezone || ''].map((value) => this.csvValue(value)).join(','),
      ['Currency', currency].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total claims', totalClaimsCreated].map((value) => this.csvValue(value)).join(','),
      ['Total used claims', totalClaimsConsumed].map((value) => this.csvValue(value)).join(','),
      ['Issued claims consumed by period end', totalIssuedClaimsConsumed].map((value) => this.csvValue(value)).join(','),
      ['Weighted issued conversion %', `${weightedClaimConversion.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Orders with claims', totalOrdersWithClaims].map((value) => this.csvValue(value)).join(','),
      ['Total net influenced sales', totalSalesFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total discount', totalDiscountFromClaims.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted discount rate (% of gross promo sales)', `${weightedDiscountRate.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Average discount per promo order', averageDiscountPerPromoOrder.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Promotions with discount rate over 15%', highCostPromotions].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'issued_claim_conversion' || header.key === 'discount_rate') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (
              header.key === 'sales_from_claims' ||
              header.key === 'discount_from_claims' ||
              header.key === 'average_discount'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      `promotion_performance_${meta.dateFrom || periodMode}_${meta.dateTo || ''}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.props.data || [];
    const meta = this.props.meta || {};
    const { order, orderBy } = this.state;
    const sortedRows = orderBy
      ? [...rows].sort((a, b) => {
          const getValue = (row) => {
            if (orderBy === 'promotion_name') {
              return String(row.promotion_name || '').toLocaleLowerCase();
            }
            if (orderBy === 'issued_claim_conversion') {
              return Number(
                row.issued_claim_conversion !== undefined
                  ? row.issued_claim_conversion
                  : row.claim_conversion || 0
              );
            }
            return Number(row[orderBy] || 0);
          };
          const aValue = getValue(a);
          const bValue = getValue(b);
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        })
      : rows;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => this.handleRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );
    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.25, sm: 2 },
            '&:last-child': { pb: { xs: 1.25, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.4} alignItems="center">
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#111827',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    Promotion performance
                  </Typography>
                  <Tooltip
                    title="Conversion follows claims issued in the selected period through period end. Cost above 15% is highlighted red."
                    arrow
                    describeChild
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                  >
                    <IconButton
                      size="small"
                      aria-label="About promotion performance"
                      sx={{ p: 0.25, color: '#64748b' }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <TableContainer
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: 'auto',
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.06)',
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: { xs: 1040, lg: 920 },
                  '& .MuiTableCell-root': {
                    fontSize: { xs: '0.7rem', sm: '0.78rem', lg: '0.7rem', xl: '0.74rem' },
                    py: { xs: 0.7, sm: 0.9 },
                    px: { xs: 0.75, sm: 1, lg: 0.55, xl: 0.7 },
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableCell-root:first-of-type': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    bgcolor: '#ffffff',
                  },
                  '& .MuiTableCell-head': {
                    fontWeight: 800,
                    color: '#1a237e',
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableCell-head:first-of-type': {
                    zIndex: 3,
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiTableRow-hover:hover .MuiTableCell-body:first-of-type': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{sortLabel('promotion_name', 'Promotion')}</TableCell>
                    <TableCell align="right">{sortLabel('claims_created', 'Claims')}</TableCell>
                    <Tooltip title={(meta.definitions && meta.definitions.claims_consumed) || 'Claims consumed during the selected period.'} arrow>
                      <TableCell align="right">{sortLabel('claims_consumed', 'Used in period')}</TableCell>
                    </Tooltip>
                    <Tooltip title={(meta.definitions && meta.definitions.issued_claim_conversion) || 'Issued claims consumed by period end divided by claims issued in the period.'} arrow>
                      <TableCell align="right">{sortLabel('issued_claim_conversion', 'Issued conversion')}</TableCell>
                    </Tooltip>
                    <TableCell align="right">{sortLabel('orders_with_claims', 'Orders')}</TableCell>
                    <TableCell align="right">{sortLabel('sales_from_claims', 'Net sales')}</TableCell>
                    <TableCell align="right">{sortLabel('discount_from_claims', 'Discount')}</TableCell>
                    <Tooltip title={(meta.definitions && meta.definitions.discount_rate) || 'Red indicates a promotion discount above 15% of gross promotion-linked sales.'} arrow>
                      <TableCell align="right">{sortLabel('discount_rate', 'Gross disc. %')}</TableCell>
                    </Tooltip>
                    <TableCell align="right">{sortLabel('average_discount', 'Avg.')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sortedRows.map((row, index) => {
                    const claimConversion = Number(
                      row.issued_claim_conversion !== undefined
                        ? row.issued_claim_conversion
                        : row.claim_conversion || 0
                    );
                    const discountRate = Number(row.discount_rate || 0);
                    const promotionName = row.promotion_name || 'Unknown';

                    return (
                      <TableRow key={`${row.promotion_iref || 'promo'}-${index}`} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: '#111827',
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          <Tooltip title={promotionName} arrow>
                            <Box component="span">{promotionName}</Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_created || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.claims_consumed || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right" sx={{ minWidth: { xs: 120, lg: 100 } }}>
                          <Stack direction="row" spacing={{ xs: 1, lg: 0.65 }} alignItems="center" justifyContent="flex-end">
                            <Box sx={{ width: { xs: 54, lg: 38 } }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(claimConversion, 100)}
                                sx={{
                                  height: 6,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: '#1a237e',
                                  },
                                }}
                              />
                            </Box>

                            <Box sx={{ minWidth: 42, fontWeight: 800 }}>
                              {formatPercent(claimConversion)}
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          {thousandsSeparator(Number(row.orders_with_claims || 0).toFixed(0))}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.sales_from_claims)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.discount_from_claims)}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: discountRate > 15 ? '#b91c1c' : '#111827',
                          }}
                        >
                          {formatPercent(discountRate)}
                        </TableCell>

                        <TableCell align="right">
                          {formatMoney(row.average_discount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No promotion performance data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default PromotionsPerformanceByPromotion;
